/* ==========================================================================
   Product tabs.

   The swipe itself is CSS — a scroll-snap track. This file only does the four
   things CSS cannot:

     1. Height. All three panels are mounted at once, so the track would stand
        as tall as the TALLEST and leave a screenful of dead space under the
        short ones. The height is written from the open panel, and interpolated
        between the two neighbours mid-drag so the box grows into the panel you
        are pulling toward rather than snapping when you let go.
     2. Moving the track when a tab is clicked.
     3. Marking which tab is open, and keeping the closed panels out of the tab
        order (they are visually clipped, so a keyboard landing inside one would
        scroll to something nobody can see).
     4. Telling the bar when it has become stuck, which `position: sticky`
        itself reports nowhere.

   With this file blocked the track is height:auto, all three panels are on the
   page, the tabs are anchors that jump to them, and everything still works.
   ========================================================================== */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    var track = root.querySelector('[data-cs-tabtrack]');
    var bar = root.querySelector('[data-cs-tabbar]');
    var sentinel = root.querySelector('[data-cs-tabsentinel]');
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-cs-tabpanel]'));
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-cs-tab]'));
    if (!track || panels.length === 0) return;

    root.classList.add('cs-tabs--js');
    /* -1, not 0. setActive() returns early when asked for the index it is
       already on, so starting at 0 meant the opening call did nothing and the
       first tab was never marked — invisible while the Liquid hard-coded
       aria-selected="true" on it, and glaring the moment that tab could be
       removed. Starting outside the range forces the first call to apply. */
    var active = -1;
    var frame = 0;

    function innerOf(panel) {
      return panel.querySelector('[data-cs-tabinner]') || panel;
    }

    /* A PANEL's width, never the track's. The track bleeds to the screen edges
       with a negative margin and pays it back as padding, so clientWidth counts
       that padding and comes out one gutter pair wider than a panel. Dividing
       scrollLeft by it made the fractional index top out at 1.9 instead of 2,
       so the settled height was always a few pixels short of the open panel and
       clipped its last row. The snap offsets are multiples of the panel width;
       measure that. */
    function step() {
      return panels[0].getBoundingClientRect().width || track.clientWidth;
    }

    /* offsetHeight of the INNER element, not the panel: the panel is a flex
       child of a track whose height this function is about to set, so reading
       the panel would feed last frame's answer straight back in. */
    function heightOf(i) {
      var p = panels[Math.max(0, Math.min(panels.length - 1, i))];
      return innerOf(p).offsetHeight;
    }

    function sizeToScroll() {
      var w = step();
      if (!w) return;
      var pos = track.scrollLeft / w;
      var i = Math.floor(pos);
      var f = pos - i;
      var h = heightOf(i) * (1 - f) + heightOf(i + 1) * f;
      track.style.height = Math.round(h) + 'px';
    }

    function setActive(i) {
      i = Math.max(0, Math.min(panels.length - 1, i));
      if (i === active) return;
      active = i;
      tabs.forEach(function (t, n) {
        t.setAttribute('aria-selected', n === i ? 'true' : 'false');
      });
      panels.forEach(function (p, n) {
        /* inert, not aria-hidden: these panels hold links, and hiding focusable
           content from assistive tech while leaving it tabbable is worse than
           doing nothing. inert removes it from both at once. */
        if (n === i) p.removeAttribute('inert');
        else p.setAttribute('inert', '');
      });
    }

    function onScroll() {
      if (frame) return;
      frame = requestAnimationFrame(function () {
        frame = 0;
        sizeToScroll();
        var w = step();
        if (w) setActive(Math.round(track.scrollLeft / w));
      });
    }

    function goTo(i, smooth) {
      var w = step();
      if (!w) return;
      track.scrollTo({ left: i * w, behavior: smooth && !reduce ? 'smooth' : 'auto' });
      /* Set it now as well as on the resulting scroll: an instant jump inside a
         track that is already at that offset fires no scroll event at all, and
         the tab would stay unmarked. */
      setActive(i);
      sizeToScroll();
    }

    /* On a real tab switch, bring the top of the newly opened panel under the
       sticky bar. All panels share the track's top (they sit side by side), so
       the target is the track top less the header and bar. Only pulls UP when
       the reader has scrolled past it — never yanks a fully visible section. */
    function scrollPanelTop() {
      var headerH = header ? header.offsetHeight : 0;
      var barH = bar ? bar.offsetHeight : 0;
      var trackTop = window.pageYOffset + track.getBoundingClientRect().top;
      var target = Math.max(0, trackTop - headerH - barH);
      if (window.pageYOffset > target + 4) {
        window.scrollTo({ top: target, behavior: reduce ? 'auto' : 'smooth' });
      }
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function (e) {
        e.preventDefault();
        goTo(i, true);
        scrollPanelTop();
      });
      tab.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowRight') next = i + 1;
        else if (e.key === 'ArrowLeft') next = i - 1;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        if (next === null) return;
        next = Math.max(0, Math.min(tabs.length - 1, next));
        e.preventDefault();
        tabs[next].focus();
        goTo(next, true);
        scrollPanelTop();
      });
    });

    track.addEventListener('scroll', onScroll, { passive: true });

    /* A panel's height changes after its images decode and again on every
       reflow. Watching the inners keeps the track honest without polling. */
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(function () { sizeToScroll(); });
      panels.forEach(function (p) { ro.observe(innerOf(p)); });
    }
    window.addEventListener('resize', function () {
      /* Width changed, so the snap offsets moved with it. Re-anchor on the open
         panel before re-measuring or the track lands between two panels. */
      goTo(active, false);
    });
    window.addEventListener('load', sizeToScroll);

    /* ------------------------------------------------------------------
       Park the bar UNDER the header, wherever the header currently is.

       The bar was already sticking correctly in both directions — measured
       barTop: 0 whether you arrived from above or below. The problem was that
       it stuck *behind* the header: Dawn's header is `sticky` at z-index 10
       (raised so the mobile menu wins over page content) and 65px tall, so it
       covered 65px of an 80px bar and only a sliver showed. Scrolling up looked
       like the bar had stopped sticking.

       A fixed 65px offset is wrong, because this header is "on scroll up": it
       translates itself off-screen while you read downward, and the bar would
       then float below a 65px band of nothing. So the offset tracks the
       header's live bottom edge instead — 0 while it is away, its height while
       it is in. One getBoundingClientRect per animation frame, no more.
       ------------------------------------------------------------------ */
    var header = document.querySelector('.section-header');
    var topFrame = 0;
    function syncTop() {
      if (!header) return;
      var edge = header.getBoundingClientRect().bottom;
      root.style.setProperty('--cs-tabs-top', Math.max(0, Math.round(edge)) + 'px');
    }
    function queueSyncTop() {
      if (topFrame) return;
      topFrame = requestAnimationFrame(function () {
        topFrame = 0;
        syncTop();
      });
    }
    if (header) {
      window.addEventListener('scroll', queueSyncTop, { passive: true });
      window.addEventListener('resize', queueSyncTop);
      syncTop();
    }

    if (sentinel && window.IntersectionObserver && bar) {
      new IntersectionObserver(function (entries) {
        bar.classList.toggle('is-stuck', !entries[0].isIntersecting);
      }, { threshold: 0 }).observe(sentinel);
    }

    setActive(0);
    panels.forEach(function (p, n) { if (n !== 0) p.setAttribute('inert', ''); });
    sizeToScroll();
  }

  function boot() {
    document.querySelectorAll('[data-cs-tabs]').forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Theme editor: a section re-render replaces the markup wholesale. */
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-cs-tabs]');
    if (root) init(root);
  });
})();
